import { emitter } from 'shared/helpers/mitt';

const FORBIDDEN_FALLBACK_MESSAGE =
  'You do not have permission to perform this action.';

/**
 * Handles pipeline API errors, showing a user-friendly toast for 403 Forbidden responses.
 * Use this in catch blocks of pipeline store actions and component API calls.
 *
 * @param {Error} error - The axios error object
 * @param {Function} [t] - Optional vue-i18n translation function.
 * @returns {Error} - Re-throws the error after handling
 */
// A recusa de sair de uma etapa terminal vem como erro de validação em
// `pipeline_stage`. O texto que o servidor devolve é escrito para quem consome a
// API — manda fazer um POST — e não serve para quem arrastou um card na tela.
// Detectamos o caso pelo conteúdo e trocamos por uma instrução humana.
const REOPEN_HINT = /deal_status\/reopen/;

const primeiroErroDeValidacao = error => {
  const dados = error?.response?.data;
  const erros = dados?.errors;
  if (typeof dados?.error === 'string') return dados.error;
  if (typeof erros === 'string') return erros;
  if (Array.isArray(erros)) return erros[0];
  if (erros && typeof erros === 'object') {
    const primeiro = Object.values(erros).flat()[0];
    if (typeof primeiro === 'string') return primeiro;
  }
  return null;
};

export function handlePipelineError(error, t = null) {
  // 422 era completamente SILENCIOSO: o board revertia o card para a coluna de
  // origem e nada aparecia na tela. Relato de cliente 2026-08-24 — arrastar um
  // card de "Venda Perdida" para outra etapa devolvia 422 em
  // `/pipeline_cards/:id/move_to_stage` e o card simplesmente voltava sozinho,
  // sem mensagem nenhuma. O usuário concluía que a tela estava quebrada.
  if (error?.response?.status === 422) {
    const bruto = primeiroErroDeValidacao(error);
    const appI18n =
      typeof window === 'undefined'
        ? null
        : // eslint-disable-next-line no-underscore-dangle
          window.__APP_I18N__;
    const translate = t || appI18n?.global?.t;
    let message;
    if (bruto && REOPEN_HINT.test(bruto)) {
      message = translate
        ? translate('PIPELINE.ERRORS.REQUIRES_REOPEN')
        : 'This card is closed as won/lost. Reopen the deal from the card panel before moving it.';
    } else if (bruto) {
      message = translate
        ? translate('PIPELINE.ERRORS.MOVE_REJECTED', { reason: bruto })
        : bruto;
    }
    if (message) {
      emitter.emit('newToastMessage', {
        message,
        action: { type: 'error' },
      });
      // Marca o erro para quem o recapturar não empilhar um segundo toast
      // genérico por cima deste (PipelineTab tinha o seu próprio).
      error.pipelineToastShown = true;
    }
  }
  if (error?.response?.status === 403) {
    // Store actions cannot use `useI18n`, so reuse the dashboard-wide i18n
    // instance. Keeping the key as the last-resort value is preferable to a
    // locale-specific hardcoded sentence during early application startup.
    const appI18n =
      typeof window === 'undefined'
        ? null
        : // eslint-disable-next-line no-underscore-dangle
          window.__APP_I18N__;
    const translate = t || appI18n?.global?.t;
    const message = translate
      ? translate('PIPELINE.ERRORS.FORBIDDEN')
      : FORBIDDEN_FALLBACK_MESSAGE;

    emitter.emit('newToastMessage', {
      message,
      action: { type: 'error' },
    });
  }
  throw error;
}
